import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HardHat, X, FloppyDisk, Warning, LinkSimple } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EQUIPMENT, findEquipment } from "@/lib/operationsCatalog";
import { useFrontOptions } from "@/lib/useFrontOptions";
import { useDevSectionOptions } from "@/lib/useDevSections";
import ComboInput from "@/components/ComboInput";
import { useOpsProjects, getOpsProject } from "@/components/OpsProjectSelector";
import { enqueueShift } from "@/lib/offlineQueue";
import EquipmentPicker from "@/components/EquipmentPicker";

// Cache in-memory de las obras vigentes del Plan por Obra — evita hits
// repetidos al abrir el dialog varias veces en el mismo turno.

const num = (v) => (v === "" || v === null || Number.isNaN(Number(v)) ? "" : Number(v));

// Iter117: mirror del mapping backend `_OP_RATES` / `_HELPER_RATES` en
// `finance_router.py`. Se usa aquí SOLO para mostrar preview de $ ganados
// mientras el operador captura; el monto final siempre lo calcula el backend
// contra `finance_tarifas` (fuente única de verdad).
//   { equipment_type: [{ rate_key, metric_key|null, unit, role: 'op'|'helper' }] }
const PAY_MAP = {
  jumbo:       [{ rate: "barrenacion_jumbero", m: "metros",   u: "ml", role: "op",     label: "Barrenación · Jumbero" }],
  voladuras:   [{ rate: "voladuras_jumbero",   m: "avance_m", u: "ml", role: "op",     label: "Voladuras · Jumbero" }],
  anclador:    [
    { rate: "ancla_jumbero",    m: "pernos",   u: "pz", role: "op",     label: "Anclaje · Boltero" },
    { rate: "malla_jumbero",    m: "malla_m2", u: "pz", role: "op",     label: "Enmallado · Boltero" },
    { rate: "ancla_ayudante",   m: "pernos",   u: "pz", role: "helper", label: "Anclaje · Ayudante" },
    { rate: "malla_ayudante",   m: "malla_m2", u: "pz", role: "helper", label: "Enmallado · Ayudante" },
  ],
  scoop:       [
    { rate: "acarreo_viaje",    m: "ciclos", u: "viaje", role: "op", label: "Acarreo · Viaje" },
    { rate: "hora_scoop",       m: null,     u: "h",     role: "op", label: "Hora Scoop" },
  ],
  camion:      [{ rate: "acarreo_viaje",   m: "ciclos", u: "viaje", role: "op", label: "Acarreo · Viaje" }],
  retro:       [{ rate: "hora_retro",      m: null,     u: "h",     role: "op", label: "Hora Retro" }],
  telehandler: [{ rate: "hora_telehandler",m: null,     u: "h",     role: "op", label: "Hora Telehandler" }],
  motoniv:     [{ rate: "hora_motoniv",    m: null,     u: "h",     role: "op", label: "Hora Motoniveladora" }],
};

// Duración en horas entre dos strings HH:MM (soporta cruce de medianoche)
const _hhmmDur = (a, b) => {
  if (!a || !b) return 0;
  try {
    const [h1, m1] = a.split(":").map((x) => parseInt(x, 10));
    const [h2, m2] = b.split(":").map((x) => parseInt(x, 10));
    let d = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (d < 0) d += 24 * 60;
    return Math.max(d / 60, 0);
  } catch { return 0; }
};

// Horas efectivas derivadas de los horómetros (según tipo de equipo)
const computeEffectiveHours = (eq, horometros, demoras) => {
  const h = horometros || {};
  let raw = 0;
  if (eq.needsHorometerEP) {
    // Bolter / Jumbo: eléctrico = horas operadas, percusión = tiempo de percusión
    const eIni = Number(h.horometro_electrico_inicial); const eFin = Number(h.horometro_electrico_final);
    if (Number.isFinite(eIni) && Number.isFinite(eFin) && eFin > eIni) raw = eFin - eIni;
  } else if (eq.needsHorometer) {
    const ini = Number(h.horometro_inicial); const fin = Number(h.horometro_final);
    if (Number.isFinite(ini) && Number.isFinite(fin) && fin > ini) raw = fin - ini;
  }
  // Descontar demoras acumuladas
  const totalDelay = (demoras || []).reduce((s, d) => s + _hhmmDur(d.inicio, d.fin), 0);
  return Math.max(raw - totalDelay, 0);
};

// Claves de horómetros dentro de metrics (para separar al heredar del operador)
const HORO_KEYS = [
  "horometro_inicial", "horometro_final",
  "horometro_electrico_inicial", "horometro_electrico_final",
  "horometro_percusion_inicial", "horometro_percusion_final",
];

// Copia los datos del turno del OPERADOR al formulario del ayudante
const applyEntryToForm = (base, e) => {
  const m = { ...(e.metrics || {}) };
  const horometros = {};
  HORO_KEYS.forEach((k) => { if (m[k] !== undefined) { horometros[k] = m[k]; delete m[k]; } });
  const demoras = Array.isArray(m.demoras) ? m.demoras : [];
  delete m.demoras; delete m.demora_inicio; delete m.demora_fin; delete m.demora_motivo;
  return {
    ...base,
    date: e.date, shift: e.shift,
    equipment_code: e.equipment_code || "", area: e.area || "",
    hours: e.hours ?? "", hours_manual: true,
    metrics: { ...base.metrics, ...m }, horometros, demoras,
  };
};

const buildInitial = (eq, user) => {
  const m = {};
  if (eq.needsOrigin) { m.origen = ""; m.destino = ""; m.distancia_m = ""; }
  if (eq.needsFuel) m.diesel_lt = "";
  if (eq.servicesCatalog) m.tipo = "";
  eq.metrics.forEach((f) => { m[f.k] = ""; });
  return {
    date: new Date().toISOString().slice(0, 10),
    shift: "dia",
    equipment_type: eq.v,
    equipment_code: "",
    operator_id: user?.employee_id || "",
    operator_name: user?.name || "",
    helper_name: "",
    hours: "",
    hours_manual: false,   // si true, el usuario editó las horas y no se auto-calcula
    area: "",
    notes: "",
    metrics: m,
    horometros: {},
    demoras: [],           // [{inicio: "HH:MM", fin: "HH:MM", motivo: ""}]
    end_status: "operativo",    // "operativo" | "no_operativo" — estatus del equipo a fin de turno
    end_location: "",           // ubicación física donde queda el equipo (rampa, taller, cámara…)
    end_reason: "",             // motivo si no_operativo (falla mecánica, reparación, etc.)
  };
};

/**
 * Diálogo simplificado "Capturar mi turno" — pensado para operador/mecánico
 * en móvil o desktop. Auto-selecciona al usuario logueado como operador
 * principal (usando su vínculo Nómina), oculta campos de auditoría y solo
 * pide lo esencial: equipo · área · horas · producción del equipo.
 */
export default function MyShiftDialog({ open, onOpenChange, onSaved, defaultType = "jumbo" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [type, setType] = useState(defaultType);
  const [form, setForm] = useState(() => buildInitial(findEquipment(defaultType) || EQUIPMENT[0], user));
  const opsProjects = useOpsProjects();
  const [extraProjects, setExtraProjects] = useState([]);
  const [newProj, setNewProj] = useState("");
  const allProjects = [...opsProjects, ...extraProjects.filter((e) => !opsProjects.some((p) => p.id === e.id))];
  const createProject = async () => {
    try {
      const r = await api.post("/ops-projects", { name: newProj.trim() });
      setExtraProjects((s) => [...s, r.data]); setProjectId(r.data.id); setNewProj("");
      toast.success(`Proyecto "${r.data.name}" creado`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const [projectId, setProjectId] = useState(getOpsProject());
  // Default = proyecto asignado al empleado en RR. HH. (si no hay filtro global activo)
  useEffect(() => {
    if (!open || projectId) return;
    api.get("/me/workspace").then((r) => { const pid = r.data?.employee?.project_id; if (pid) setProjectId(pid); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);      // registros recientes del mismo equipo_code
  const [helperMode, setHelperMode] = useState(false);   // "Capturo como ayudante"
  const [linkables, setLinkables] = useState([]);        // turnos de operadores para vincular
  const [linkedEntry, setLinkedEntry] = useState(null);  // registro del operador seleccionado
  const pendingLink = useRef(null);                      // link diferido cuando cambia el tipo de equipo
  const [maintEquipCatalog, setMaintEquipCatalog] = useState([]);  // maint_equipment para dropdown
  const frontOptions = useFrontOptions();                          // obras del Plan por Obra
  const sectionOptions = useDevSectionOptions();                   // secciones de desarrollo (Finanzas)
  // Iter113: sólo admins pueden alternar el nombre del operador para capturar
  // información en nombre de otra persona (p.ej. cuando el operador no puede
  // acceder a la app). El toggle deja al descubierto un Select con todos los
  // empleados activos y sobrescribe operator_id/operator_name en el payload.
  const [overrideOperator, setOverrideOperator] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  // Iter117: tarifas vigentes de Finanzas → preview de $ en vivo mientras
  // el operador captura. Fuente única de verdad = backend, esto sólo pinta.
  const [destajoRates, setDestajoRates] = useState(null);

  useEffect(() => {
    if (!open || destajoRates) return;
    api.get("/finance/tarifas")
      .then((r) => setDestajoRates(r.data?.tarifas?.destajo || {}))
      .catch(() => setDestajoRates({}));
  }, [open, destajoRates]);

  useEffect(() => {
    if (!open || !isAdmin || employeeOptions.length > 0) return;
    api.get("/hr/employees?active=true")
      .then((r) => setEmployeeOptions((r.data || []).map((e) => ({
        id: e.id, name: e.name, position: e.position || "", crew: e.crew || "",
      }))))
      .catch(() => setEmployeeOptions([]));
  }, [open, isAdmin, employeeOptions.length]);

  // Cargamos el catalogo de equipos de Mantenimiento la primera vez que se
  // abre el dialog -- alimenta el autocomplete de "Codigo equipo". Se
  // guarda en estado para no repetir peticiones.
  useEffect(() => {
    if (!open || maintEquipCatalog.length > 0) return;
    api.get("/maintenance/equipment/lite-public")
      .then((r) => setMaintEquipCatalog(r.data || []))
      .catch(() => setMaintEquipCatalog([]));
  }, [open, maintEquipCatalog.length]);

  // Sugerencias filtradas por prefijo, restringidas al equipment_type activo
  // cuando el catalogo trae info de tipo (matching heuristico por nombre).
  const maintEquipSuggestions = useMemo(() => {
    const q = (form?.equipment_code || "").trim().toLowerCase();
    if (q.length < 1 || !maintEquipCatalog.length) return [];
    return maintEquipCatalog
      .filter((mq) => {
        const label = `${mq.code || ""} ${mq.name || ""}`.toLowerCase();
        return label.includes(q) && (mq.code || "").toLowerCase() !== q;
      })
      .slice(0, 6);
  }, [form?.equipment_code, maintEquipCatalog]);

  const eq = findEquipment(type) || EQUIPMENT[0];

  // Preview de destajo — replica el mapping backend contra las tarifas actuales.
  // Se recalcula en cada tecleo. Cuando el operador captura como ayudante
  // (helperMode + linked) muestra sólo lo del rol ayudante; de lo contrario
  // asumimos rol operador principal.
  const earningsPreview = useMemo(() => {
    if (!destajoRates || !PAY_MAP[eq.v]) return null;
    const m = form.metrics || {};
    const hrs = Number(form.hours || 0);
    const role = helperMode && linkedEntry ? "helper" : "op";
    const rows = [];
    let total = 0;
    for (const it of PAY_MAP[eq.v]) {
      if (it.role !== role) continue;
      const qty = it.m === null ? hrs : Number(m[it.m] || 0);
      if (!qty) continue;
      const rate = Number(destajoRates[it.rate] || 0);
      const amount = qty * rate;
      total += amount;
      rows.push({ label: it.label, qty, unit: it.u, rate, amount });
    }
    return { rows, total, role };
  }, [destajoRates, eq.v, form.metrics, form.hours, helperMode, linkedEntry]);

  // Reset del estado de ayudante al abrir el diálogo
  useEffect(() => {
    if (!open) return;
    setHelperMode(false);
    setLinkedEntry(null);
    setLinkables([]);
    pendingLink.current = null;
    setOverrideOperator(false);
  }, [open]);

  // Al cambiar equipo o al abrir el diálogo, resetear el formulario
  // (si hay un vínculo de ayudante pendiente, se aplica sobre el form limpio)
  useEffect(() => {
    if (!open) return;
    const base = buildInitial(eq, user);
    const link = pendingLink.current;
    if (link && link.equipment_type === eq.v) {
      pendingLink.current = null;
      setForm(applyEntryToForm(base, link));
    } else {
      setForm(base);
    }
    setHistory([]);
  }, [open, type, user, eq]);

  // Cargar turnos de operadores del mismo día/turno para el modo ayudante
  useEffect(() => {
    if (!open || !helperMode) return;
    api.get(`/operations/entries?date=${form.date}&shift=${form.shift}`)
      .then((r) => setLinkables((r.data || []).filter((e) =>
        e.operator_name && e.operator_name !== user?.name &&
        (!e.helper_name || e.helper_name === user?.name))))
      .catch(() => setLinkables([]));
  }, [open, helperMode, form.date, form.shift]);

  const selectLink = (entry) => {
    if (!entry) return;
    setLinkedEntry(entry);
    if (entry.equipment_type !== type) {
      pendingLink.current = entry;      // el efecto de reset aplicará los datos
      setType(entry.equipment_type);
    } else {
      setForm(applyEntryToForm(buildInitial(eq, user), entry));
    }
  };

  const unlink = () => {
    setLinkedEntry(null);               // conserva los datos copiados para edición libre
    toast.info("Desvinculado — ahora capturas tu propio registro con los datos heredados");
  };

  const locked = helperMode && !!linkedEntry;

  // Cargar historial reciente del mismo código de equipo para avisos automáticos
  useEffect(() => {
    if (!open || !form.equipment_code) return;
    const t = setTimeout(() => {
      api.get(`/operations/entries?equipment_code=${encodeURIComponent(form.equipment_code)}&limit=15`)
        .then((r) => setHistory(r.data || []))
        .catch(() => setHistory([]));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.equipment_code]);

  // ---------------- Avisos automáticos (no bloqueantes) ----------------
  const warnings = useMemo(() => {
    const out = [];
    const h = form.horometros || {};
    const m = form.metrics || {};
    if (eq.needsHorometer) {
      const last = history
        .map((x) => Number((x.metrics || {}).horometro_final))
        .filter((v) => Number.isFinite(v) && v > 0)
        .sort((a, b) => b - a)[0];
      const ini = Number(h.horometro_inicial); const fin = Number(h.horometro_final);
      if (Number.isFinite(last) && Number.isFinite(ini) && ini > 0 && ini < last) {
        out.push({ level: "warn", text: `Horómetro inicial (${ini}) es menor que el último final registrado (${last})` });
      }
      if (Number.isFinite(ini) && Number.isFinite(fin) && ini > 0 && fin > 0 && fin < ini) {
        out.push({ level: "warn", text: `Horómetro final (${fin}) debe ser mayor al inicial (${ini})` });
      }
    }
    if (eq.needsHorometerEP) {
      for (const suffix of ["electrico", "percusion"]) {
        const kIni = `horometro_${suffix}_inicial`;
        const kFin = `horometro_${suffix}_final`;
        const ini = Number(h[kIni]); const fin = Number(h[kFin]);
        const last = history
          .map((x) => Number((x.metrics || {})[kFin]))
          .filter((v) => Number.isFinite(v) && v > 0)
          .sort((a, b) => b - a)[0];
        if (Number.isFinite(last) && Number.isFinite(ini) && ini > 0 && ini < last) {
          out.push({ level: "warn", text: `${suffix.toUpperCase()}: inicial (${ini}) menor al último final (${last})` });
        }
        if (Number.isFinite(ini) && Number.isFinite(fin) && ini > 0 && fin > 0 && fin < ini) {
          out.push({ level: "warn", text: `${suffix.toUpperCase()}: final debe ser mayor al inicial` });
        }
      }
    }
    if (history.length >= 5 && eq.metrics.length > 0) {
      const primary = eq.metrics[0];
      const currVal = Number(m[primary.k]);
      const sample = history.map((x) => Number((x.metrics || {})[primary.k])).filter((v) => Number.isFinite(v) && v > 0);
      if (sample.length >= 5 && Number.isFinite(currVal) && currVal > 0) {
        const avg = sample.reduce((a, v) => a + v, 0) / sample.length;
        if (currVal < avg * 0.7) {
          out.push({ level: "info", text: `${primary.l} (${currVal}) por debajo del 70% del promedio del equipo (${avg.toFixed(1)})` });
        }
      }
    }
    return out;
  }, [form, history, eq]);

  const setMetric = (k, v) => setForm((f) => ({ ...f, metrics: { ...f.metrics, [k]: v } }));
  const setHoro = (k, v) => setForm((f) => {
    const next = { ...f, horometros: { ...f.horometros, [k]: v } };
    if (!next.hours_manual) {
      const auto = computeEffectiveHours(eq, next.horometros, next.demoras);
      if (auto > 0) next.hours = Number(auto.toFixed(2));
    }
    return next;
  });

  // ---------------- Demoras (múltiples) ----------------
  const addDelay = () => setForm((f) => ({ ...f, demoras: [...(f.demoras || []), { inicio: "", fin: "", motivo: "" }] }));
  const removeDelay = (i) => setForm((f) => {
    const list = [...(f.demoras || [])];
    list.splice(i, 1);
    const next = { ...f, demoras: list };
    if (!next.hours_manual) next.hours = Number(computeEffectiveHours(eq, next.horometros, list).toFixed(2)) || "";
    return next;
  });
  const setDelay = (i, k, v) => setForm((f) => {
    const list = [...(f.demoras || [])];
    list[i] = { ...list[i], [k]: v };
    const next = { ...f, demoras: list };
    if (!next.hours_manual && (k === "inicio" || k === "fin")) {
      const auto = computeEffectiveHours(eq, next.horometros, list);
      next.hours = auto > 0 ? Number(auto.toFixed(2)) : "";
    }
    return next;
  });

  const save = async () => {
    // Modo ayudante vinculado → nos adjuntamos al registro del operador
    // (sin crear un registro nuevo, para no duplicar la producción en KPIs)
    if (locked) {
      if (!navigator.onLine) {
        toast.warning("Vincularte como ayudante requiere conexión. Reintenta al reconectar o desvincula para capturar tu propio registro.");
        return;
      }
      setBusy(true);
      try {
        await api.post(`/operations/entries/${linkedEntry.id}/attach-helper`, {
          helper_id: user?.employee_id || "",
          helper_name: user?.name || "",
        });
        toast.success(`Vinculado como ayudante al turno de ${linkedEntry.operator_name} ✓`);
        onSaved?.();
        onOpenChange(false);
      } catch (e) { toast.error(apiError(e)); }
      finally { setBusy(false); }
      return;
    }
    if (!form.equipment_code || !form.hours) {
      toast.warning("Completa código del equipo y horas efectivas");
      return;
    }
    if (!projectId) { toast.warning("Indica a qué depósito / proyecto se carga la captura"); return; }
    setBusy(true);
    const cleanDelays = (form.demoras || []).filter((d) => d.inicio && d.fin);
    const payload = {
      project_id: projectId || undefined,
      date: form.date,
      shift: form.shift,
      equipment_type: eq.v,
      equipment_code: form.equipment_code,
      operator_id: form.operator_id,
      operator_name: form.operator_name,
      helper_name: form.helper_name,
      hours: Number(form.hours || 0),
      area: form.area,
      notes: form.notes,
      metrics: {
        ...form.metrics,
        ...form.horometros,
        // Estatus de fin de turno + ubicación (visible por todos en MyWork)
        end_status: form.end_status || "operativo",
        end_location: (form.end_location || "").trim(),
        end_reason: (form.end_reason || "").trim(),
        ...(cleanDelays.length > 0 ? {
          // Compat con lógica antigua (una sola demora) + nueva lista completa
          demora_inicio: cleanDelays[0].inicio,
          demora_fin: cleanDelays[0].fin,
          demora_motivo: cleanDelays.map((d) => d.motivo).filter(Boolean).join(" · "),
          demoras: cleanDelays,
        } : {}),
      },
      penalizaciones: {},   // el operador NO puede aplicar cláusulas (solo auditor)
    };
    try {
      if (!navigator.onLine) {
        // Sin red → guardamos en la cola IndexedDB, se sube al reconectar
        await enqueueShift(payload);
        toast.success("Sin conexión · Turno guardado offline y se subirá al reconectar", { duration: 4500 });
        onSaved?.();
        onOpenChange(false);
        return;
      }
      await api.post("/operations/entries", payload);
      toast.success("Turno capturado ✓");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      // Errores de red (timeout, DNS, 502…) → también van a la cola offline
      const isNetErr = !e?.response || e?.code === "ECONNABORTED" || e?.message?.includes("Network");
      if (isNetErr) {
        try {
          await enqueueShift(payload);
          toast.success("Red intermitente · Turno guardado offline y se reintentará", { duration: 4500 });
          onSaved?.();
          onOpenChange(false);
          return;
        } catch { /* fallthrough al toast de error */ }
      }
      toast.error(apiError(e));
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-slate-900">
            <HardHat size={18} weight="fill" className="text-amber-500" />
            Capturar mi turno
          </DialogTitle>
        </DialogHeader>

        {/* Operador — captura auto o (admin) alternable a otro empleado */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-2.5 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-900 flex items-center justify-center font-bold text-sm">
            {(form.operator_name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1.5">
              Operador
              {overrideOperator && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-black uppercase tracking-widest">
                  captura admin
                </span>
              )}
            </p>
            {overrideOperator ? (
              <Select
                value={form.operator_id || "__self__"}
                onValueChange={(v) => {
                  if (v === "__self__") {
                    setForm({ ...form, operator_id: user?.employee_id || "", operator_name: user?.name || "" });
                    return;
                  }
                  const emp = employeeOptions.find((e) => e.id === v);
                  if (emp) setForm({ ...form, operator_id: emp.id, operator_name: emp.name });
                }}
              >
                <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="my-shift-operator-select">
                  <SelectValue placeholder="Elegir empleado…" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">
                  <SelectItem value="__self__">— Yo mismo ({user?.name || "admin"}) —</SelectItem>
                  {employeeOptions.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}{e.position ? ` · ${e.position}` : ""}{e.crew ? ` · ${e.crew}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="my-shift-operator-name">{form.operator_name || "(sin ficha en Nómina)"}</p>
            )}
          </div>
          {isAdmin && (
            <div className="flex flex-col items-end gap-0.5 shrink-0">
              <span className="text-[9px] uppercase font-bold text-slate-500">Otro operador</span>
              <Switch
                checked={overrideOperator}
                onCheckedChange={(v) => {
                  setOverrideOperator(v);
                  if (!v) setForm({ ...form, operator_id: user?.employee_id || "", operator_name: user?.name || "" });
                }}
                data-testid="my-shift-operator-override-toggle"
              />
            </div>
          )}
        </div>
        {overrideOperator && (
          <p className="text-[10px] italic text-amber-800 -mt-2 px-1" data-testid="my-shift-override-hint">
            Estás capturando este turno en nombre de <b>{form.operator_name || "otro operador"}</b>. Quedará
            registrado con tu usuario como quien creó el registro (auditoría) pero con ese operador como responsable de la producción.
          </p>
        )}

        {/* Modo ayudante — vincularse al turno del operador */}
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-md border border-emerald-200 bg-emerald-50/50">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
              <LinkSimple size={13} weight="bold" className="text-emerald-700" /> Capturo como ayudante
            </p>
            <p className="text-[10px] text-slate-500">Vincúlate al turno de tu operador y hereda equipo, horómetros y producción.</p>
          </div>
          <Switch checked={helperMode} data-testid="my-shift-helper-toggle"
                  onCheckedChange={(v) => { setHelperMode(v); if (!v) setLinkedEntry(null); }} />
        </div>
        {helperMode && (
          <div className="p-2.5 rounded-md bg-emerald-50/60 border border-emerald-200 space-y-2" data-testid="my-shift-helper-panel">
            <Label className="text-[10px] uppercase font-bold text-emerald-800">
              Turno del operador · {form.date} · {form.shift === "dia" ? "Día" : "Noche"}
            </Label>
            <Select value={linkedEntry?.id || ""} onValueChange={(id) => selectLink(linkables.find((x) => x.id === id))}>
              <SelectTrigger data-testid="my-shift-link-select">
                <SelectValue placeholder={linkables.length ? "Selecciona el turno del operador…" : "Sin turnos de operadores capturados"} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {linkables.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {(e.equipment_code || findEquipment(e.equipment_type)?.label || e.equipment_type)} · {e.operator_name}{e.area ? ` · ${e.area}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {linkables.length === 0 && (
              <p className="text-[11px] italic text-slate-500">
                No hay turnos de operadores para esta fecha/turno. Cambia fecha/turno arriba o pide a tu operador que capture primero.
              </p>
            )}
            {linkedEntry && (
              <div className="flex items-start justify-between gap-2 pt-1 border-t border-emerald-100">
                <p className="text-[11px] text-emerald-900" data-testid="my-shift-linked-banner">
                  ✓ Vinculado a <b>{linkedEntry.operator_name}</b>. Al guardar quedarás como <b>ayudante en su registro</b> (sin duplicar producción). Los datos heredados quedan bloqueados.
                </p>
                <Button size="sm" variant="outline" onClick={unlink} className="h-7 text-[11px] shrink-0" data-testid="my-shift-unlink-btn">
                  Desvincular
                </Button>
              </div>
            )}
          </div>
        )}

        <fieldset disabled={locked} className={`grid gap-4 ${locked ? "opacity-70" : ""}`}>
        {/* Depósito / Proyecto — siempre visible */}
        <div className="border-2 border-blue-900/20 bg-blue-50/60 rounded-lg p-3" data-testid="my-shift-project-box">
          <Label className="overline-label text-blue-900">Depósito / Proyecto *</Label>
          <select value={projectId} onChange={(e) => { if (e.target.value === "__new__") { setNewProj(" "); return; } setProjectId(e.target.value); }}
                  className={`w-full h-10 border rounded-md px-2 text-sm bg-white font-semibold ${!projectId ? "border-amber-400" : "border-blue-300"}`} data-testid="my-shift-project">
            <option value="">— ¿A qué depósito / proyecto se carga esta captura? —</option>
            {allProjects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
            <option value="__new__">+ Nuevo depósito / proyecto…</option>
          </select>
          {allProjects.length === 0 && !newProj && (
            <p className="text-[11px] text-slate-600 mt-1">Aún no hay proyectos. Créalo aquí con "+ Nuevo" o en <b>Ajustes › Proyectos de Operaciones</b>.</p>
          )}
          {newProj !== "" && (
            <div className="flex gap-2 mt-2">
              <Input autoFocus value={newProj.trim() === "" ? "" : newProj} placeholder="Nombre del depósito / proyecto (ej. San Pablo)"
                     onChange={(e) => setNewProj(e.target.value || " ")} className="h-9 bg-white" data-testid="my-shift-project-new-name" />
              <Button type="button" size="sm" className="h-9 bg-blue-900 text-white" disabled={!newProj.trim()} onClick={createProject} data-testid="my-shift-project-new-save">Crear</Button>
              <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => setNewProj("")}>Cancelar</Button>
            </div>
          )}
        </div>
        {/* Equipo */}
        <div>
          <Label className="overline-label">Tipo de equipo</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="my-shift-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {EQUIPMENT.map((e) => (
                <SelectItem key={e.v} value={e.v}>{e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fila fecha / turno / horas */}
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="overline-label">Fecha</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="my-shift-date" /></div>
          <div><Label className="overline-label">Turno</Label>
            <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
              <SelectTrigger data-testid="my-shift-shift"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="dia">Día</SelectItem>
                <SelectItem value="noche">Noche</SelectItem>
              </SelectContent>
            </Select></div>
          <div><Label className="overline-label">Horas ef.</Label>
            <div className="relative">
              <Input
                type="number" step="0.1" value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value, hours_manual: true })}
                data-testid="my-shift-hours"
                className={form.hours_manual ? "" : "bg-blue-50/60"}
              />
              {!form.hours_manual && form.hours !== "" && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold uppercase text-blue-800 pointer-events-none">auto</span>
              )}
            </div>
            {form.hours_manual && (
              <button type="button" onClick={() => setForm({ ...form, hours_manual: false, hours: Number(computeEffectiveHours(eq, form.horometros, form.demoras).toFixed(2)) || "" })}
                className="text-[10px] text-blue-900 hover:underline mt-0.5"
                data-testid="my-shift-hours-reset">Recalcular auto</button>
            )}
          </div>
        </div>

        {/* Equipo + área */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="overline-label">Código equipo</Label>
            <EquipmentPicker
              mode="code"
              value={form.equipment_code}
              onChange={(v, eq) => setForm({
                ...form,
                equipment_code: v,
                area: (eq && eq.area) ? eq.area : form.area,
              })}
              dataTestId="my-shift-code"
              placeholder="Selecciona del catálogo…"
              filterFn={(mq) => {
                // Iter108: si el catálogo trae `type`, filtramos por el tipo
                // activo (jumbo/scoop/etc.). Los equipos SIN `type` (catálogos
                // legacy) SIEMPRE se muestran para no ocultar equipos válidos.
                const t = (mq.type || "").toLowerCase().trim();
                if (!t) return true;
                return t === (type || "").toLowerCase().trim();
              }}
            />
          </div>
          <div><Label className="overline-label">Área / zona</Label>
            <ComboInput value={form.area} placeholder="Elige obra del Plan o escribe" options={frontOptions}
                        onChange={(v) => setForm({ ...form, area: v })} testid="my-shift-area" emptyText="Sin obras en Plan por Obra" />
            {frontOptions.length > 0 && (
              <p className="text-[10px] text-slate-400 italic mt-0.5">
                {frontOptions.length} obra{frontOptions.length !== 1 ? "s" : ""} activa{frontOptions.length !== 1 ? "s" : ""} del Plan por Obra — puedes elegir o escribir libre
              </p>
            )}
          </div>
        </div>

        {/* Horómetros */}
        {eq.needsHorometer && (
          <div className="grid grid-cols-2 gap-2 p-2.5 rounded-md bg-slate-50 border border-slate-200">
            <p className="col-span-2 overline-label text-blue-900">⏱ Horómetro diésel</p>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Inicial</Label>
              <Input type="number" step="any" value={form.horometros.horometro_inicial ?? ""}
                     onChange={(e) => setHoro("horometro_inicial", num(e.target.value))} data-testid="my-shift-hi" /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Final</Label>
              <Input type="number" step="any" value={form.horometros.horometro_final ?? ""}
                     onChange={(e) => setHoro("horometro_final", num(e.target.value))} data-testid="my-shift-hf" /></div>
            <EffectiveHoursHint eq={eq} horometros={form.horometros} demoras={form.demoras} />
          </div>
        )}
        {eq.needsHorometerEP && (
          <div className="p-2.5 rounded-md bg-blue-50 border border-blue-200 space-y-2">
            <p className="overline-label text-blue-900">⚡ Horómetros por equipo</p>
            <p className="text-[11px] text-blue-900 -mt-1">
              Las <b>horas efectivas</b> se calculan del <b>horómetro eléctrico</b> (final − inicial − demoras).
              El <b>horómetro de percusión</b> mide el tiempo real de barrenado (para KPIs de eficiencia).
            </p>
            {/* Eléctrico */}
            <div className="rounded border border-blue-200 bg-white p-2">
              <p className="text-[10px] uppercase font-black text-blue-900 mb-1.5">⚡ Eléctrico · horas operadas</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase font-bold text-slate-500">Inicial</Label>
                  <Input type="number" step="any" value={form.horometros.horometro_electrico_inicial ?? ""}
                         onChange={(e) => setHoro("horometro_electrico_inicial", num(e.target.value))}
                         data-testid="my-shift-horometro_electrico_inicial" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-slate-500">Final</Label>
                  <Input type="number" step="any" value={form.horometros.horometro_electrico_final ?? ""}
                         onChange={(e) => setHoro("horometro_electrico_final", num(e.target.value))}
                         data-testid="my-shift-horometro_electrico_final" />
                </div>
              </div>
            </div>
            {/* Percusión */}
            <div className="rounded border border-blue-200 bg-white p-2">
              <p className="text-[10px] uppercase font-black text-blue-900 mb-1.5">🔨 Percusión · tiempo de barrenado</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase font-bold text-slate-500">Inicial</Label>
                  <Input type="number" step="any" value={form.horometros.horometro_percusion_inicial ?? ""}
                         onChange={(e) => setHoro("horometro_percusion_inicial", num(e.target.value))}
                         data-testid="my-shift-horometro_percusion_inicial" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-slate-500">Final</Label>
                  <Input type="number" step="any" value={form.horometros.horometro_percusion_final ?? ""}
                         onChange={(e) => setHoro("horometro_percusion_final", num(e.target.value))}
                         data-testid="my-shift-horometro_percusion_final" />
                </div>
              </div>
            </div>
            <EffectiveHoursHint eq={eq} horometros={form.horometros} demoras={form.demoras} />
          </div>
        )}

        {/* Movilidad */}
        {eq.needsOrigin && (
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Origen</Label>
              <Input value={form.metrics.origen ?? ""} onChange={(e) => setMetric("origen", e.target.value)} data-testid="my-shift-origen" /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Destino</Label>
              <Input value={form.metrics.destino ?? ""} onChange={(e) => setMetric("destino", e.target.value)} data-testid="my-shift-destino" /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Distancia (m)</Label>
              <Input type="number" value={form.metrics.distancia_m ?? ""} onChange={(e) => setMetric("distancia_m", num(e.target.value))} data-testid="my-shift-dist" /></div>
          </div>
        )}
        {eq.needsFuel && (
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Diésel (lt)</Label>
            <Input type="number" step="any" value={form.metrics.diesel_lt ?? ""} onChange={(e) => setMetric("diesel_lt", num(e.target.value))} data-testid="my-shift-diesel" /></div>
        )}

        {/* Métricas específicas del equipo */}
        {eq.metrics.length > 0 && (
          <div className="grid grid-cols-2 gap-2 p-2.5 rounded-md bg-blue-50/60 border border-blue-200">
            <p className="col-span-2 overline-label text-blue-900">Producción del turno</p>
            {eq.metrics.map((f) => {
              const isText = f.type === "text";
              return (
                <div key={f.k}>
                  <Label className="text-[10px] uppercase font-bold text-slate-500">{f.l}</Label>
                  {f.list === "dev-sections" ? (
                    <ComboInput value={form.metrics[f.k] ?? ""} onChange={(v) => setMetric(f.k, v)} options={sectionOptions}
                                placeholder="Elige la sección" testid={`my-shift-m-${f.k}`} emptyText="Sin secciones: créalas en Ajustes" />
                  ) : (
                  <Input
                    type={isText ? "text" : "number"}
                    step={isText ? undefined : (f.step || "any")}
                    value={form.metrics[f.k] ?? ""}
                    onChange={(e) => setMetric(f.k, isText ? e.target.value : num(e.target.value))}
                    data-testid={`my-shift-m-${f.k}`}
                  />
                  )}
                </div>
              );
            })}
            {/* Iter117: Preview de destajo en vivo — vincula captura → tarifa Finanzas */}
            {earningsPreview && earningsPreview.rows.length > 0 && (
              <div className="col-span-2 mt-1 p-2 rounded-md bg-emerald-50 border border-emerald-300"
                   data-testid="my-shift-earnings-preview">
                <div className="flex items-center justify-between mb-1">
                  <p className="overline-label text-emerald-800">
                    💰 Vas ganando {earningsPreview.role === "helper" ? "(como ayudante)" : "(como operador)"}
                  </p>
                  <p className="font-heading font-black text-lg text-emerald-800 tabular-nums" data-testid="my-shift-earnings-total">
                    ${earningsPreview.total.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <ul className="text-[10.5px] space-y-0.5 text-emerald-900">
                  {earningsPreview.rows.map((r, i) => (
                    <li key={i} className="flex justify-between gap-2">
                      <span className="truncate">{r.label}: <b>{r.qty}</b> {r.unit} × ${r.rate}</span>
                      <span className="font-mono font-bold shrink-0">${r.amount.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[9px] italic text-emerald-700 mt-1">
                  Estimado con tarifas vigentes de Finanzas. El monto final se calcula al cierre del turno con cláusulas del auditor.
                </p>
              </div>
            )}
          </div>
        )}

        {eq.needsHelper && (
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Ayudante (nombre)</Label>
            <Input value={form.helper_name} placeholder="Opcional"
                   onChange={(e) => setForm({ ...form, helper_name: e.target.value })}
                   data-testid="my-shift-helper" /></div>
        )}

        {/* Demoras (múltiples) — solo si el equipo las contempla */}
        {eq.needsDelays && (
          <div className="p-2.5 rounded-md bg-slate-50 border border-slate-200 space-y-2" data-testid="my-shift-delays">
            <div className="flex items-center justify-between">
              <p className="overline-label text-slate-700">⏸ Demoras del turno · descuentan de horas efectivas</p>
              <Button size="sm" variant="outline" onClick={addDelay} className="h-7 text-xs gap-1" data-testid="my-shift-add-delay">
                + Agregar demora
              </Button>
            </div>
            {(form.demoras || []).length === 0 && (
              <p className="text-[11px] italic text-slate-400">Sin demoras registradas.</p>
            )}
            {(form.demoras || []).map((d, i) => (
              <div key={i} className="grid grid-cols-[80px_80px_1fr_28px] gap-1.5 items-end" data-testid={`my-shift-delay-${i}`}>
                <div>
                  <Label className="text-[9px] uppercase font-bold text-slate-500">Inicio</Label>
                  <Input type="time" value={d.inicio} onChange={(e) => setDelay(i, "inicio", e.target.value)} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[9px] uppercase font-bold text-slate-500">Fin</Label>
                  <Input type="time" value={d.fin} onChange={(e) => setDelay(i, "fin", e.target.value)} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[9px] uppercase font-bold text-slate-500">Motivo</Label>
                  <Input value={d.motivo} placeholder="Falla eléctrica, cambio de broca…" onChange={(e) => setDelay(i, "motivo", e.target.value)} className="h-8 text-xs" />
                </div>
                <button type="button" onClick={() => removeDelay(i)} className="h-8 w-7 flex items-center justify-center text-slate-400 hover:text-red-600" aria-label="Quitar demora">
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Notas */}
        <div>
          <Label className="overline-label">Notas del turno</Label>
          <Textarea rows={2} value={form.notes} placeholder="Observaciones libres…"
            onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="my-shift-notes" />
        </div>

        {/* Estatus a fin de turno + ubicación — visible por todo el equipo */}
        <div className="p-3 rounded-md border border-slate-300 bg-slate-50 space-y-3" data-testid="my-shift-end-block">
          <p className="overline-label text-slate-700 flex items-center gap-1.5">
            <HardHat size={12} weight="bold" className="text-slate-700" />
            ¿Cómo queda el equipo al terminar tu turno?
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setForm({ ...form, end_status: "operativo", end_reason: "" })}
              className={`h-11 rounded-md border-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                form.end_status === "operativo"
                  ? "bg-emerald-600 text-white border-emerald-700"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-emerald-50"}`}
              data-testid="my-shift-end-operativo">
              ✓ Operativo
            </button>
            <button type="button" onClick={() => setForm({ ...form, end_status: "no_operativo" })}
              className={`h-11 rounded-md border-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                form.end_status === "no_operativo"
                  ? "bg-red-600 text-white border-red-700"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-red-50"}`}
              data-testid="my-shift-end-no-operativo">
              ✗ No operativo
            </button>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold text-slate-500">Ubicación al cierre</Label>
            <Input value={form.end_location} placeholder="ej. Rampa -450, taller, cámara 12…"
              onChange={(e) => setForm({ ...form, end_location: e.target.value })}
              data-testid="my-shift-end-location" />
          </div>
          {form.end_status === "no_operativo" && (
            <div data-testid="my-shift-end-reason-block">
              <Label className="text-[10px] uppercase font-bold text-red-700">Motivo (falla, mantenimiento, reparación…)</Label>
              <Input value={form.end_reason} placeholder="Descripción breve…"
                onChange={(e) => setForm({ ...form, end_reason: e.target.value })}
                data-testid="my-shift-end-reason" />
            </div>
          )}
          <p className="text-[10px] italic text-slate-500">
            Esta información se comparte con supervisores, administrativos y operadores del siguiente turno en Mi Trabajo → Turnos del equipo.
          </p>
        </div>
        </fieldset>

        <p className="text-[10px] italic text-slate-500">
          Las cláusulas de riesgo solo puede aplicarlas el auditor (admin, jefatura, supervisor) desde la sección Operaciones.
        </p>

        {/* Avisos automáticos (no bloqueantes) */}
        {warnings.length > 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-2.5 space-y-1" data-testid="my-shift-warnings">
            <p className="overline-label text-amber-700 flex items-center gap-1.5">
              <Warning size={12} weight="fill" /> {warnings.length === 1 ? "1 aviso" : `${warnings.length} avisos`}
            </p>
            {warnings.map((w, i) => (
              <p key={i} className={`text-[11px] flex items-start gap-1.5 ${w.level === "warn" ? "text-amber-900 font-semibold" : "text-slate-700"}`}>
                <span>{w.level === "warn" ? "⚠" : "ℹ"}</span> {w.text}
              </p>
            ))}
            <p className="text-[10px] italic text-slate-500 pt-1 border-t border-amber-100">
              Puedes guardar de todas formas; los avisos quedan visibles para el auditor.
            </p>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="my-shift-cancel">
            <X size={13} weight="bold" /> Cancelar
          </Button>
          <Button onClick={save} disabled={busy || (helperMode && !linkedEntry)} className="bg-blue-900 hover:bg-blue-800 text-white" data-testid="my-shift-save">
            <FloppyDisk size={13} weight="bold" /> {busy ? "Guardando…" : locked ? "Vincularme como ayudante" : "Guardar turno"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Muestra en vivo la fórmula usada para calcular las horas efectivas:
 * `H_efec = Horómetro final − Horómetro inicial − Σ demoras`.
 * Prioriza el horómetro eléctrico si el equipo lo tiene (needsHorometerEP).
 */
function EffectiveHoursHint({ eq, horometros, demoras }) {
  const h = horometros || {};
  const list = demoras || [];
  let iniK, finK, srcLabel;
  if (eq.needsHorometerEP) {
    iniK = "horometro_electrico_inicial"; finK = "horometro_electrico_final";
    srcLabel = "eléctrico";
  } else if (eq.needsHorometer) {
    iniK = "horometro_inicial"; finK = "horometro_final";
    srcLabel = "diésel";
  } else {
    return null;
  }
  const ini = Number(h[iniK]); const fin = Number(h[finK]);
  const delayH = list.reduce((s, d) => s + _hhmmDur(d.inicio, d.fin), 0);
  const raw = Number.isFinite(ini) && Number.isFinite(fin) && fin > ini ? fin - ini : 0;
  const eff = Math.max(raw - delayH, 0);
  const ready = raw > 0;
  return (
    <p
      data-testid="my-shift-effective-hint"
      className={`col-span-2 mt-1 text-[11px] font-mono px-2 py-1.5 rounded ${
        ready ? "bg-white border border-blue-300 text-blue-900" : "bg-slate-100 text-slate-400"
      }`}
    >
      <span className="opacity-70">H. efectivas ({srcLabel}):</span>{" "}
      {ready
        ? <>{fin.toFixed(2)} − {ini.toFixed(2)}{delayH > 0 ? ` − ${delayH.toFixed(2)} h demoras` : ""} = <b>{eff.toFixed(2)} h</b></>
        : "captura horómetro inicial y final para calcular"}
    </p>
  );
}
